// The PAINT tool: recolor EXISTING voxels using the same five shapes as the
// SKETCH/Create tool (voxel, extrude, circle, freeform lasso, box), routed by
// uiState.shapeMode. It's the Create tool's geometry with Erase's targeting
// (operate on the voxel under the cursor) and Draw's colour (the active
// palette) — instead of placing or deleting, every shape sets the swept
// existing voxels to uiState.currentPaletteIndex.
//
// Like Sketch, it owns the trigger-press start for all five shapes and the
// freeform commit (close the lasso → recolor the enclosed region). The other
// shapes finalize through the generic idle/endTransaction tail in main. There
// is no draw/erase action here — Paint always recolors.

import type * as THREE from "three";
import { uiState } from "../ui/state";
import { type PickTarget, type CellBounds } from "../interaction/pick";
import { type MirrorConfig, setMirror } from "../interaction/mirror";
import { startVoxelPaint } from "../interaction/handlers/voxel-paint";
import { startExtrude } from "../interaction/handlers/extrude";
import { startCircle } from "../interaction/handlers/circle";
import { startBox } from "../interaction/handlers/box";
import { type VoxelGrid } from "../model/voxel-grid";
import { type ToolRegistry } from "../app/tool-registry";

// Paint never originates a selection mask the way extrude-from-selected does;
// it recolors whatever it sweeps. Shared empty set avoids a per-gesture alloc.
const NO_PROTECTED: ReadonlySet<string> = new Set();

export interface PaintToolDeps {
  tools: ToolRegistry;
  pickFor: (i: number) => PickTarget | null; // the controller's last-frame pick
  // Same gesture-target resolution as Sketch: a voxel hit → that object; a
  // room-surface hit → the room-aligned object. Marks it so main routes the
  // per-frame drag handlers + commit at that object until release.
  resolveGestureTarget: (i: number) => { grid: VoxelGrid; group: THREE.Object3D };
  cellBounds: CellBounds; // live work-volume bounds (mutated in place by resize)
  mirrorConfig: () => MirrorConfig;
  screenDriven: () => boolean; // desktop drives extrude depth from the screen ray
}

export function installPaintTool(deps: PaintToolDeps): void {
  deps.tools.register({
    id: "paint",
    claims: () => uiState.topMode === "paint",
    start: (i) => {
      const pick = deps.pickFor(i);
      if (!pick) return;
      const { grid: tGrid, group: tGroup } = deps.resolveGestureTarget(i);
      const shape = uiState.shapeMode;
      const palette = uiState.currentPaletteIndex;

      // Arm mirror so a symmetric model recolors symmetrically.
      setMirror(deps.mirrorConfig());

      if (shape === "voxel") {
        startVoxelPaint(pick, i, "recolor", tGrid, deps.cellBounds, palette);
      } else if (shape === "extrude") {
        // Recolor needs a voxel face to flood-fill from; a bare surface has no
        // region to recolor, so it's a no-op (same as Sketch's extrude rule).
        if (pick.kind === "voxel") {
          startExtrude(
            pick,
            i,
            "recolor",
            tGrid,
            tGroup,
            deps.cellBounds,
            deps.screenDriven(),
            NO_PROTECTED,
            palette,
          );
        }
      } else if (shape === "circle") {
        startCircle(pick, i, "recolor", tGrid, deps.cellBounds, palette);
      } else if (shape === "freeform") {
        // Freeform is a desktop multi-click polygon lasso built OUTSIDE the
        // press-drag gesture seam (see src/app/desktop-input.ts +
        // interaction/handlers/freeform.ts). No gesture starts here — returning
        // keeps it from falling through to BOX.
        return;
      } else {
        // BOX
        startBox(pick, i, "recolor", tGrid, deps.cellBounds, palette);
      }
    },
  });
}
